import { isEmptyOrSpaces } from '../../Utilities/Helpers';
import { BrowserStorageTimeEnum } from './Models/BrowserStorageTimeEnum';
import { BrowserStorageType } from './Models/BrowserStorageTypeEnum';
import { LocalStorageData } from './Models/LocalStorageData';
import { SessionStorageData } from './Models/SessionStorageData';

/**
 * Provides Functions to use session Storage.
 * Keep in mind that session Storage supports
 * only 5 MB for partner center which will be used
 * across all the micro-sites.
 */
export default class BrowserStorage {
  private static instance: BrowserStorage;

  private sessionStorage = sessionStorage;
  private localStorage = localStorage;

  /**
   * Time limit for Local Storage.
   * Duration - 8 hours.
   * */
  private localStorageTimeDefault = BrowserStorageTimeEnum.eight_hours;

  /**
   * Time Limit for Session Storage
   * Duration - 2 hours.
   */
  private sessionStorageTimeDefault = BrowserStorageTimeEnum.two_hours;

  /** Suffix Key that will be added for all the Keys. */
  private suffixKey = 'support:';

  public static getInstance = () => {
    if (!BrowserStorage.instance) {
      BrowserStorage.instance = new BrowserStorage();
    }
    return BrowserStorage.instance;
  };

  private constructor() {}

  public setItem(
    key: string,
    body: any,
    overrideSuffix: boolean = false,
    type: BrowserStorageType = BrowserStorageType.temporary
  ) {
    return this.setItemInStorage(
      key,
      body,
      overrideSuffix,
      type === BrowserStorageType.temporary ? this.sessionStorage : this.localStorage
    );
  }

  public setItemInStorage(key: string, body: any, overrideSuffix: boolean = false, storage: Storage) {
    if (!isEmptyOrSpaces(key) && body !== void 0 && body !== null) {
      //[TODO]: Once the flight for the Panel is restored with correct naming we can
      // remove the special logic for panel
      const modifiedKey = overrideSuffix ? key : this.suffixKey + key;

      const storageData: SessionStorageData = {
        body: body,
        timespan: new Date().getTime(),
      };

      try {
        storage.setItem(modifiedKey, JSON.stringify(storageData));
        return true;
      } catch (err) {
        return false;
      }
    }

    return false;
  }

  public getItem(
    key: string,
    overrideSuffix: boolean = false,
    type: BrowserStorageType = BrowserStorageType.temporary,
    timespan?: BrowserStorageTimeEnum
  ) {
    return this.getItemInStorage(
      key,
      overrideSuffix,
      type === BrowserStorageType.temporary ? this.sessionStorage : this.localStorage,
      timespan ? timespan : BrowserStorageType.temporary ? this.sessionStorageTimeDefault : this.localStorageTimeDefault
    );
  }

  public getItemInStorage(
    key: string,
    overrideSuffix: boolean = false,
    storage: Storage,
    timespan: BrowserStorageTimeEnum
  ) {
    if (!isEmptyOrSpaces(key)) {
      const modifiedKey = overrideSuffix ? key : this.suffixKey + key;

      try {
        const storageData = storage.getItem(modifiedKey);
        if (storageData) {
          const storageDataParsed: SessionStorageData | LocalStorageData = JSON.parse(storageData);
          const currentDateTime = new Date().getTime();

          if (
            currentDateTime - storageDataParsed.timespan > 0 &&
            currentDateTime - storageDataParsed.timespan < timespan &&
            storageDataParsed.body
          ) {
            return storageDataParsed.body;
          }
          this.removeItem(modifiedKey, false, storage);
        }
      } catch (err) {
        return null;
      }
    }
    return null;
  }

  /**
   * Removes Support Suffix + Key or Key (if you override the Suffix) from Session Storage
   * @param key Name of the Key specified.
   * @param overrideSuffix Determines whether to override the Suffix or Not
   */
  public removeItem(key: string, overrideSuffix: boolean = false, storage: Storage) {
    if (!isEmptyOrSpaces(key)) {
      const modifiedKey = overrideSuffix ? key : this.suffixKey + key;

      storage.removeItem(modifiedKey);
    }
  }

  /**
   * Removes All the Storage Keys starting with Support Suffix
   */
  public clearAll() {
    const propertyNames = Object.getOwnPropertyNames(this.sessionStorage);

    propertyNames.forEach(propertyName => {
      if (propertyName.startsWith(this.suffixKey)) {
        this.removeItem(propertyName, true, this.sessionStorage);
        this.removeItem(propertyName, true, this.localStorage);
      }
    });
  }
}
